import { FunctionComponent, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faThumbTack } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { DeepResearchResponseSection } from "@/pages/Private/redux/deepResearch/schema";

import {
	PinnableFact,
	usePinnedResearchFacts,
} from "@/pages/Private/redux/deepResearch/usePinnedResearchFacts";

import { FitAssessmentBadge } from "../FitAssessmentBadge";
import { DeepResearchResponseData, formatSectionTitle } from "../deepResearchText.utils";
import { ResearchSectionCard } from "./ResearchSectionCard";
import { ResearchToolbar, SectionNavItem } from "./ResearchToolbar";
import {
	buildSelectionFactKey,
	hasAnySource,
	resolveSectionKind,
	resolveShortSectionLabel,
} from "./researchSections.utils";
import { useResearchSelection } from "./useResearchSelection";

interface ResearchResultsViewProps {
	response: DeepResearchResponseData;
	prospectId?: number;
	templateName?: string;
	researchedAt: string | null;
	isCopied: boolean;
	isRerunDisabled: boolean;
	creditLabel: string;
	portalContainer?: HTMLElement | null;
	onCopy: () => void;
	onRerun: () => void;
	/** Opens the lead's Chat tab from a pinned fact (PREC-3680). */
	onRequestSequence?: () => void;
}

export const ResearchResultsView: FunctionComponent<ResearchResultsViewProps> = ({
	response,
	prospectId,
	templateName,
	researchedAt,
	isCopied,
	isRerunDisabled,
	creditLabel,
	portalContainer,
	onCopy,
	onRerun,
	onRequestSequence,
}) => {
	const { t } = useTranslation();
	const bodyRef = useRef<HTMLDivElement>(null);
	const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const [activeSection, setActiveSection] = useState<string | null>(null);
	const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

	const { pinnedKeys, pinnedCount, isPinning, togglePin } = usePinnedResearchFacts(
		prospectId,
		response.id
	);
	const { selection, clearSelection } = useResearchSelection(bodyRef);

	const sections = useMemo(
		() =>
			Object.entries(response.response as Record<string, DeepResearchResponseSection>).sort(
				([, a], [, b]) => a.order - b.order
			),
		[response.response]
	);

	const navItems = useMemo<SectionNavItem[]>(
		() =>
			sections.map(([key]) => ({
				key,
				label: resolveShortSectionLabel(key, formatSectionTitle(key)),
			})),
		[sections]
	);

	const showsNoSourcesHint = useMemo(
		() => !hasAnySource(sections.map(([, section]) => section)),
		[sections]
	);

	/**
	 * Jumping to a section expands and scrolls to it, and leaves every other
	 * section exactly as the user left it — the previous behaviour collapsed them
	 * all, which is what made it impossible to get back (PREC-3680).
	 */
	const handleSectionClick = useCallback((key: string) => {
		setActiveSection(key);
		setCollapsed((prev) => ({ ...prev, [key]: false }));
		sectionRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "start" });
	}, []);

	const handleTogglePin = useCallback(
		(fact: PinnableFact) => {
			void togglePin(fact);
		},
		[togglePin]
	);

	const handlePinSelection = useCallback(() => {
		if (!selection) {
			return;
		}

		void togglePin({
			factKey: buildSelectionFactKey(selection.text),
			factLabel: t("profileTracker.deepResearch.selectionLabel"),
			factText: selection.text,
		});

		window.getSelection()?.removeAllRanges();
		clearSelection();
	}, [selection, togglePin, clearSelection, t]);

	return (
		<div className="flex flex-col gap-2.5">
			<ResearchToolbar
				activeSection={activeSection}
				creditLabel={creditLabel}
				isCopied={isCopied}
				isRerunDisabled={isRerunDisabled}
				lastResearchedAt={researchedAt}
				sections={navItems}
				onCopy={onCopy}
				onRerun={onRerun}
				onSectionClick={handleSectionClick}
			/>

			<FitAssessmentBadge fitAssessment={response.fitAssessment} fitReason={response.fitReason} />

			{pinnedCount > 0 && (
				<p
					className="flex items-center gap-1.5 text-xs font-semibold text-brand-light"
					data-testid="research-pinned-count"
				>
					<Icon className="h-3 w-3" icon={faThumbTack} />
					{t("profileTracker.deepResearch.pinnedCount", { count: pinnedCount })}
				</p>
			)}

			{showsNoSourcesHint && (
				<p className="text-xs text-text-tertiary" data-testid="research-no-sources-hint">
					{t("profileTracker.deepResearch.noSourcesHint")}
				</p>
			)}

			<div ref={bodyRef} className="flex flex-col gap-2">
				{sections.map(([key, section], index) => (
					<ResearchSectionCard
						key={key}
						ref={(element) => {
							sectionRefs.current[key] = element;
						}}
						isCollapsed={collapsed[key] ?? false}
						isPinning={isPinning}
						isSelecting={!!selection}
						kind={resolveSectionKind(key)}
						pinnedKeys={pinnedKeys}
						portalContainer={portalContainer}
						position={index + 1}
						section={section}
						sectionKey={key}
						title={formatSectionTitle(key)}
						onRequestSequence={onRequestSequence}
						onToggleCollapse={() =>
							setCollapsed((prev) => ({ ...prev, [key]: !(prev[key] ?? false) }))
						}
						onTogglePin={handleTogglePin}
					/>
				))}
			</div>

			{selection && (
				<div
					className="z-[1300] flex items-center gap-2 rounded-lg border border-brand-light bg-white p-1.5 pl-2.5 shadow-lg"
					data-testid="research-selection-cta"
					style={{
						position: "fixed",
						top: selection.top,
						left: Math.max(8, selection.right - 260),
					}}
					// The CTA must survive the mousedown that would otherwise clear the
					// selection before the click lands.
					onMouseDown={(event) => event.preventDefault()}
				>
					<span className="whitespace-nowrap text-[11px] font-bold text-text-tertiary">
						{t("profileTracker.deepResearch.wordsSelected", { count: selection.wordCount })}
					</span>
					<button
						className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-brand-light bg-white px-2 py-1 text-[11px] font-semibold text-brand-light hover:bg-light-blue"
						data-testid="research-selection-pin"
						type="button"
						onClick={handlePinSelection}
					>
						<Icon className="h-3 w-3" icon={faThumbTack} />
						{t("profileTracker.deepResearch.pinForSequence")}
					</button>
				</div>
			)}

			<div className="flex flex-wrap items-center justify-between gap-2 border-t border-border-light pt-2.5 text-[11px] text-text-tertiary">
				<span className="font-semibold text-text-meta">
					{t("profileTracker.deepResearch.template")}{" "}
					<span className="text-brand-light">{templateName ?? "—"}</span>
				</span>
				{researchedAt && (
					<span>{t("profileTracker.deepResearch.researchedOn", { date: researchedAt })}</span>
				)}
			</div>
		</div>
	);
};
